import React, {ReactNode} from 'react';

import {
  SaveToResultsStepDataName,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {
  AnalysisGraphQL,
  AnalysisViewGraphQL,
  GetAnalysisDataFrame,
  GetAnalysisPipelineStep,
  isParameterOptimizationStepType,
  isSidebarDatasetInitializationMultiOutputStepType,
} from 'analysis-tool/AnalysisTypes';
import {
  getIconAndTextForChartViewStep,
  getIconAndTextForRegressionStep,
  getIconAndTextForTableViewStep,
} from 'analysis-tool/charting/AnalysisChartingUtils';
import {
  StepDataFrameLineageNode,
  getTopLevelTableLineages,
  getTableLineageAncestorInformation,
  getTableLineageDescendantInformation,
  areStepDataFrameLineageNodesEqual,
} from 'analysis-tool/data/DatasetLineageUtils';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import {isTableStartStepType} from 'analysis-tool/data/LineageStepTypes';
import {
  allParentViewsSucceeded,
  getDefaultClientIdForView,
  getMainViewItemForView,
  getParentViewsForView,
  getViewForViewReference,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import {
  getIconForView,
  getTitleForView,
  getChildViewsForView,
  isTopLevelAnalysisView,
  isLegacyTopLevelAnalysisView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import {getDocumentIdFromStep} from 'analysis-tool/outputs/OutputsUtil';
import SendToDocumentSidebarItemTitle from 'analysis-tool/outputs/SendToDocumentSidebarItemTitle';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  DataReference,
  isStepAwareAnalysisItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {assertPipelineStepIsStepType, PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getChildSteps, getParentSteps} from 'pipelines/StepLineageUtils';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import pickDefined from 'util/pickDefined';
import removeNulls from 'util/removeNulls';
import {assertNever} from 'util/Types';

import getLayoutViewGroups from './getLayoutViewGroups';

export interface SidebarItemData {
  // If the icon should be duotone when selected, make sure it's a supported duotone icon in UI-Core Storybook
  // at https://duotone-icons--6303d99f6ffc62228a6a58f9.chromatic.com/ > Components > Icons > Duotone.
  // Otherwise it'll be rendered as a monotone icon. If it's not there, we'll need to add support for it by
  // requesting it in #design-systems.
  icon: BenchlingDuotoneIconName | BenchlingIconName;
  title: ReactNode;
  /** Main View item corresponding to this Sidebar item. Used for determining (a) which Main View item to
   * open when the Sidebar item is selected, and (b) if the Sidebar Item is already selected for rendering
   * purposes.
   */
  mainViewItem: AnalysisMainViewItem;
  childSidebarItems?: Array<SidebarItemData>;
  /**
   * Optional options for an action button to render at the end of the section devoted to this sidebar item
   */
  actionButtonOptions?: {
    stepDataReference: DataReference;
    dataFrameId: string;
  };
  subtitleText?: string;
  /**
   * Optional flag to mark an item as being disabled
   */
  disabled?: boolean;
  /**
   * Optional text to display on hover (e.g. if an item is archived)
   */
  tooltipText?: string;
  /**
   * Optional flag to mark that the item contains a FLAG_ROWS transformation
   */
  isFlaggingRows?: boolean;
  /**
   * Optional flag to mark an item as inactive, i.e. dissuade a user from clicking on it
   */
  inactive?: boolean;
}

export const SIDEBAR_FAILED_ICON = 'exclamation-circle';

/** @package */
export const BASE_FAILED_SIDEBAR_ITEM_DATA: Pick<SidebarItemData, 'icon' | 'title'> = {
  icon: SIDEBAR_FAILED_ICON,
  title: 'Error',
};

export const SIDEBAR_SPINNER_ICON = 'spinner-third';

export const SIDEBAR_INACTIVE_ICON = 'ban';

/** @package */
export const INACCESSIBLE_FILE_ITEM_TEXT = '(Inaccessible file)';

/** @package */
export const INACCESSIBLE_FILE_TOOLTIP_TEXT = 'You do not have access to this file';

/** @package */
export const BASE_RUNNING_SIDEBAR_ITEM_DATA: Pick<SidebarItemData, 'icon' | 'title'> = {
  icon: SIDEBAR_SPINNER_ICON,
  title: 'Loading...',
};

/*
 *********************************
 * Start of the old sidebar code *
 *********************************
 */

function getFailedSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  return {
    ...BASE_FAILED_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.FAILED,
      stepId: step.id,
    },
  };
}

function getRunningSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  return {
    ...BASE_RUNNING_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.RUNNING,
      stepId: step.id,
    },
  };
}

function getSucceededModelingSidebarItemDatas(step: GetAnalysisPipelineStep): Array<SidebarItemData> {
  return [
    {
      icon: 'document',
      title: 'Overview',
      mainViewItem: {
        type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_OVERVIEW,
        stepId: step.id,
      },
    },
    {
      icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
      title: 'Analysis results',
      mainViewItem: {
        type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD,
        stepId: step.id,
      },
    },
  ];
}

function getSucceededPredictionSidebarItemDatas(step: GetAnalysisPipelineStep): Array<SidebarItemData> {
  return [
    {
      icon: 'crystal-ball',
      title: 'Predictions',
      mainViewItem: {type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_PREDICTION, stepId: step.id},
    },
  ];
}

const MULTI_OUPUT_DATA_FRAME_STEP_TYPES = [PipelineStepType.REGRESSION];

/**
 * Because these steps have multi-output, we return the id of the step's input
 * data frame to display in the sidebar view. Otherwise, we use the dataFrame defined by the lineage node.
 */
function getDataFrameForMainViewItem({
  step,
  data,
}: StepDataFrameLineageNode): GetAnalysisDataFrame | undefined {
  if (MULTI_OUPUT_DATA_FRAME_STEP_TYPES.includes(step.definition.stepType)) {
    return step.inputStepDatas[0].dataFrames[0];
  } else {
    return data?.dataFrame;
  }
}

/**
 * For a given lineage node, get the lineages of all of its DATASET_VIEW child steps.
 *
 * This function is used to gather all the views nested underneath a top-level dataset.
 */
function getChildDatasetViewStepsLineages(
  pipeline: PipelineGraphQL,
  lineageNode: StepDataFrameLineageNode
): Array<Array<StepDataFrameLineageNode>> {
  let childDatasetViewSteps = getChildSteps(pipeline, lineageNode.step).filter(
    ({definition: {stepType}}) => stepType === PipelineStepType.DATASET_VIEW
  );

  // Filter child steps by whether they accept the parent node's data frame as an input data frame
  if (lineageNode.data != null) {
    childDatasetViewSteps = childDatasetViewSteps.filter((step) =>
      step.inputStepDatas
        .flatMap((stepData) => stepData.dataFrames)
        .map((dataFrame) => dataFrame.id)
        .includes(lineageNode.data!.dataFrame.id)
    );
  }
  return childDatasetViewSteps.map((step) => getTableLineageDescendantInformation(pipeline, {step}).nodes);
}

/**
 * Get the sidebar item data for a lineage tree that begins with a dataset view step.
 *
 * Only the leaf steps of the lineage will be displayed in the sidebar, as any intermediate steps
 * are transformations on the dataset view's output data frame. Essentially, this collapses
 * the dataset view's lineage down into one sidebar item.
 *
 * The only step types that we expect to find at the end of a dataset view lineage are:
 * - DATASET_VIEW
 * - REGRESSION
 * - CHART_VIEW
 * - TABLE_VIEW
 *
 * If another leaf step type is encountered, this function will raise an assertion error.
 *
 * @example given `viewLineageTree` DATASET_VIEW -> FILTER_DATASET -> PIVOT_DATASET -> CHART_VIEW,
 * then the sidebar item displayed would be of the CHART_VIEW step.
 *
 * @deprecated
 * @package exported for testing
 */
export function getViewSidebarItemData(viewLineage: Array<GetAnalysisPipelineStep>): SidebarItemData {
  assert(
    viewLineage[0].definition.stepType === PipelineStepType.DATASET_VIEW,
    'Expected this function to only be called on a `viewLineage` that begins with a dataset view step.'
  );

  const lastViewStep = viewLineage.findLast(
    (step) => step.definition.stepType === PipelineStepType.DATASET_VIEW
  )!;

  const isPartialError = viewLineage.some((step) => step.statusCode === PipelineStepStatusCode.FAILED);
  const isAllError = viewLineage.every((step) => step.statusCode === PipelineStepStatusCode.FAILED);
  const isRunning =
    !isPartialError &&
    viewLineage.some((step) =>
      [PipelineStepStatusCode.RUNNING, PipelineStepStatusCode.BLOCKED].includes(step.statusCode)
    );

  const isFlaggingRows = viewLineage.some((step) => step.definition.stepType === PipelineStepType.FLAG_ROWS);

  let mainViewItem: AnalysisMainViewItem;
  let iconAndText: Pick<SidebarItemData, 'icon' | 'title'>;

  // The terminal step in the dataset view lineage, which will be displayed in the sidebar
  const leafStep = viewLineage[viewLineage.length - 1];

  switch (leafStep.definition.stepType) {
    case PipelineStepType.CHART_VIEW:
      mainViewItem = {type: AnalysisMainViewItemType.CHART, stepId: leafStep.id};
      iconAndText = getIconAndTextForChartViewStep(leafStep);
      break;
    case PipelineStepType.TABLE_VIEW:
      mainViewItem = {type: AnalysisMainViewItemType.CHART, stepId: leafStep.id};
      iconAndText = getIconAndTextForTableViewStep(leafStep);
      break;
    case PipelineStepType.REGRESSION:
      mainViewItem = {type: AnalysisMainViewItemType.REGRESSION, stepId: leafStep.id};
      iconAndText = getIconAndTextForRegressionStep(leafStep);
      break;
    default:
      mainViewItem = {
        type: AnalysisMainViewItemType.EDIT_CHART,
        stepId: lastViewStep.id,
      };
      iconAndText = {
        icon: 'analyze',
        title: 'Unfinished view...',
      };
      break;
  }

  mainViewItem = isAllError ? {type: AnalysisMainViewItemType.FAILED, stepId: leafStep.id} : mainViewItem;

  return {
    ...(isRunning
      ? BASE_RUNNING_SIDEBAR_ITEM_DATA
      : isPartialError
        ? BASE_FAILED_SIDEBAR_ITEM_DATA
        : iconAndText),
    mainViewItem,
    isFlaggingRows,
  };
}

function getDatasetRunningSidebarItemData(
  step: GetAnalysisPipelineStep,
  viewLineage: Array<StepDataFrameLineageNode>
): SidebarItemData {
  const successfulAncestorNode = viewLineage.findLast(
    (node) => node.step.statusCode === PipelineStepStatusCode.SUCCEEDED
  );

  if (successfulAncestorNode == null) {
    return getRunningSidebarItemData(step);
  } else {
    // Render DATA_FRAME view with the current step to show statusMessage, ancestry transforms, and
    // related configs, but use the last successful data frame.
    const dataFrame = getDataFrameForMainViewItem(successfulAncestorNode);
    if (dataFrame != null) {
      return {
        ...BASE_RUNNING_SIDEBAR_ITEM_DATA,
        mainViewItem: {
          type: AnalysisMainViewItemType.DATA_FRAME,
          dataFrameId: dataFrame.id,
          stepId: step.id,
        },
      };
    }

    assertAndContinue(false, 'Unexpectedly got a SUCCEEDED step with no output data frames', {
      step: successfulAncestorNode.step,
    });

    return getRunningSidebarItemData(successfulAncestorNode.step);
  }
}

/**
 * Get the sidebar item data for a lineage tree of a dataset.
 *
 * @deprecated
 */
export function getDatasetSidebarItemData(
  pipeline: PipelineGraphQL,
  node: StepDataFrameLineageNode,
  terminalNode?: StepDataFrameLineageNode
): SidebarItemData | null {
  const fullLineage = [
    ...getTableLineageAncestorInformation(pipeline, node).nodes,
    // Slice from index 1 so that we don't double-count `node`
    ...getTableLineageDescendantInformation(pipeline, node).nodes.slice(1),
  ];
  const rootNode = fullLineage[0];

  assert(
    isTableStartStepType(rootNode.step.definition.stepType),
    'Expected root node to be a top-level or view start step.'
  );

  if (rootNode.step.definition.stepType === PipelineStepType.DATASET_VIEW) {
    return getViewSidebarItemData(fullLineage.map((node) => node.step));
  }

  const stopIdx =
    terminalNode == null
      ? undefined
      : fullLineage.findIndex((node) => areStepDataFrameLineageNodesEqual(node, terminalNode)) + 1;

  assert(stopIdx !== 0, "Expected terminalNode to exist in rootNode's lineage if defined.");

  const partialLineage = fullLineage.slice(0, stopIdx);
  const isFlaggingRows = partialLineage.some(
    (node) => node.step.definition.stepType === PipelineStepType.FLAG_ROWS
  );
  const leafNode = partialLineage[partialLineage.length - 1];

  // Depending on the status of the leaf-most step, we will display one of its ancestor steps instead
  switch (leafNode.step.statusCode) {
    case PipelineStepStatusCode.BLOCKED:
    case PipelineStepStatusCode.WAITING: {
      // Hide waiting third-party steps that may show up in the UI because they were created prior to FIRST_PARTY_ANALYSES being enabled
      if (leafNode.step.definition.stepType === PipelineStepType.THIRD_PARTY) {
        assertAndContinue(
          leafNode.step.outputStepDatas.every(
            (stepData) => !stepData.dataFrames.length && !stepData.scratchFiles.length
          ),
          'Expected WAITING third-party step not to have any outputs',
          {stepId: leafNode.step.id}
        );
        return null;
      }
      // If the leaf step is blocked or waiting, then display a sidebar item for the last non-BLOCKED or non-WAITING ancestor step instead
      const unblockedAncestorNode = partialLineage.findLast(
        (node: StepDataFrameLineageNode) =>
          node.step.statusCode !== PipelineStepStatusCode.BLOCKED &&
          node.step.statusCode !== PipelineStepStatusCode.WAITING
      );
      if (
        unblockedAncestorNode == null ||
        unblockedAncestorNode.step.statusCode === PipelineStepStatusCode.SUCCEEDED
      ) {
        // This is an intermediate state where the pipeline hasn't finished updating itself yet, so it is still
        // running if a follow-up step is `BLOCKED`.
        return getDatasetRunningSidebarItemData(leafNode.step, partialLineage);
      } else {
        const itemData = getDatasetSidebarItemData(pipeline, rootNode, unblockedAncestorNode);
        return itemData == null
          ? null
          : {
              ...itemData,
              mainViewItem: isStepAwareAnalysisItem(itemData.mainViewItem)
                ? {
                    ...itemData.mainViewItem,
                    stepId: leafNode.step.id,
                  }
                : itemData.mainViewItem,
            };
      }
    }
    case PipelineStepStatusCode.FAILED: {
      // If the leaf step is failed, then display a sidebar item allowing the user to access the last
      // SUCCEEDED ancestor step instead, though with indication that the leaf step is failed
      const successfulAncestorNode = partialLineage.findLast(
        (node: StepDataFrameLineageNode) => node.step.statusCode === PipelineStepStatusCode.SUCCEEDED
      );

      if (successfulAncestorNode == null) {
        return getFailedSidebarItemData(leafNode.step);
      } else {
        // Render DATA_FRAME view with the current step to show statusMessage, ancestry transforms, and
        // related configs, but use the last successful data frame.
        const dataFrame = getDataFrameForMainViewItem(successfulAncestorNode);
        if (dataFrame != null) {
          return {
            ...BASE_FAILED_SIDEBAR_ITEM_DATA,
            mainViewItem: {
              type: AnalysisMainViewItemType.DATA_FRAME,
              dataFrameId: dataFrame.id,
              stepId: leafNode.step.id,
            },
            title: dataFrame.name,
          };
        }

        throw Error('Unexpectedly got a FAILED step with no output data frames');
      }
    }
    case PipelineStepStatusCode.RUNNING:
      // If the leaf step is READY or RUNNING, then display a "running" sidebar item. Note that READY
      // is a deprecated step status and will be removed in BNCH-90689
      return getDatasetRunningSidebarItemData(leafNode.step, partialLineage);
    case PipelineStepStatusCode.SUCCEEDED: {
      // If the leaf step is SUCCEEDED, then display a data frame sidebar item for the leaf step and
      // data frame.
      const dataFrame = getDataFrameForMainViewItem(leafNode);
      if (dataFrame == null) {
        // The dataFrame may be null if the step data is not required, in which case we shouldn't show
        // a sidebar item at all.
        return null;
      }

      const originOutputStepData = getOutputStepDataForDataFrame(leafNode.step, dataFrame.id);
      if (originOutputStepData == null) {
        throw Error(
          `Unexpectedly got a data frame that didn't come from a leaf node's output: ${leafNode.step.id}`
        );
      }

      return {
        mainViewItem: {
          type: AnalysisMainViewItemType.DATA_FRAME,
          dataFrameId: dataFrame.id,
          stepId: leafNode.step.id,
        },
        title: dataFrame.name,
        icon: ModelTypeConstants.DATA_FRAME,
        actionButtonOptions: {
          stepDataReference: {
            stepDataName: originOutputStepData.name,
            stepId: leafNode.step.id,
          },
          dataFrameId: dataFrame.id,
        },
        isFlaggingRows,
      };
    }
    default:
      throw Error(`Unsupported status code - ${assertNever(leafNode.step.statusCode)}`);
  }
}

/**
 * @deprecated
 */
export function getDatasetSectionSidebarItemDatas(pipeline: PipelineGraphQL): Array<SidebarItemData> {
  const unfilteredTopLevelTableLineages = getTopLevelTableLineages(pipeline);

  // Filter out all but one top-level dataset lineage for multi-output steps that are RUNNING to only display one sidebar item
  const topLevelTableLineages: Array<Array<StepDataFrameLineageNode>> = [];

  const includedRunningMultiOutputStepIds: Array<string> = [];
  for (const lineage of unfilteredTopLevelTableLineages) {
    const rootStep = lineage[0].step;
    if (
      !isSidebarDatasetInitializationMultiOutputStepType(rootStep.definition.stepType) ||
      rootStep.statusCode === PipelineStepStatusCode.SUCCEEDED
    ) {
      topLevelTableLineages.push(lineage);
    } else if (!includedRunningMultiOutputStepIds.includes(rootStep.id)) {
      topLevelTableLineages.push(lineage);
      includedRunningMultiOutputStepIds.push(rootStep.id);
    }
  }

  return removeNulls(
    topLevelTableLineages.map((lineage) => {
      const rootNode = lineage[0];
      const leafNode = lineage[lineage.length - 1];
      const datasetSidebarItemData = getDatasetSidebarItemData(pipeline, rootNode);

      return datasetSidebarItemData == null
        ? null
        : {
            ...datasetSidebarItemData,
            childSidebarItems: getChildDatasetViewStepsLineages(pipeline, leafNode).map((viewLineage) =>
              getViewSidebarItemData(viewLineage.map((node) => node.step))
            ),
          };
    })
  );
}

/** @package */
function getFileStepSidebarItemDatas(step: GetAnalysisPipelineStep): Array<SidebarItemData> {
  return step.outputStepDatas
    .filter((stepData) => stepData.kind === PipelineStepDataKind.FILE)
    .flatMap((stepData) =>
      stepData.scratchFiles
        .filter((scratchFile) => isInaccessible(scratchFile) || !isArchived(scratchFile))
        .map((scratchFile) => {
          if (isInaccessible(scratchFile)) {
            return {
              mainViewItem: {
                type: AnalysisMainViewItemType.FILE,
                fileId: scratchFile.invalidId,
                stepId: step.id,
              },
              title: INACCESSIBLE_FILE_ITEM_TEXT,
              tooltipText: INACCESSIBLE_FILE_TOOLTIP_TEXT,
              icon: ModelTypeConstants.FILE,
              disabled: true,
            };
          } else {
            return {
              mainViewItem: {
                type: AnalysisMainViewItemType.FILE,
                fileId: scratchFile.id,
                stepId: step.id,
              },
              title: scratchFile.filename,
              icon: ModelTypeConstants.FILE,
            };
          }
        })
    );
}

/**
 * Return `SidebarItemData`s for all top-level files in an analysis.
 *
 * Keep in sync with benchling/pipelines/steps/step_lineage_utils.py:get_top_level_file_ids
 *
 * @deprecated
 */
export function getFilesSectionSidebarItemDatas(analysis: AnalysisGraphQL): Array<SidebarItemData> {
  return analysis.execution.steps
    .filter((step) => step.definition.stepType === PipelineStepType.THIRD_PARTY)
    .flatMap((step) => getFileStepSidebarItemDatas(step));
}

/**
 * @deprecated
 */
export function getSaveToResultsSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return {
      ...BASE_FAILED_SIDEBAR_ITEM_DATA,
      mainViewItem: {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId: step.id},
    };
  } else if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const inputTableName = step.inputStepDatas[0].dataFrames[0].name;
    return {
      icon: 'table',
      title: step.outputStepDatas.find(
        (stepData) => stepData.name === SaveToResultsStepDataName.SAVED_RESULTS
      )!.dataFrames[0].name,
      mainViewItem: {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId: step.id},
      subtitleText: `From ${inputTableName} data`,
    };
  } else {
    assertAndContinue(
      [
        PipelineStepStatusCode.WAITING,
        PipelineStepStatusCode.RUNNING,
        PipelineStepStatusCode.BLOCKED,
      ].includes(step.statusCode),
      `Unexpected status code for save to results step ${step.statusCode}`
    );
    return getRunningSidebarItemData(step);
  }
}

/**
 * @deprecated
 */
export function getExportDataCatalogDatasetSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return getFailedSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const outputDataFrame = step.outputStepDatas.find(
      (stepData) => stepData.name === SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
    )!.dataFrames[0];
    return {
      icon: 'dataset',
      title: outputDataFrame.name,
      mainViewItem: {
        type: AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT,
        stepId: step.id,
        dataFrameId: outputDataFrame.id,
      },
    };
  } else {
    assertAndContinue(
      [
        PipelineStepStatusCode.WAITING,
        PipelineStepStatusCode.RUNNING,
        PipelineStepStatusCode.BLOCKED,
      ].includes(step.statusCode),
      'Unexpected status code for export data catalog dataset step',
      {
        statusCode: step.statusCode,
      }
    );
    return getRunningSidebarItemData(step);
  }
}

/**
 * @deprecated
 */
export function getSendToDocumentSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  const documentId = getDocumentIdFromStep(step);
  const icon = APIUtil.apiIdToType(documentId) === ModelTypeConstants.WORKSHEET ? 'worksheet' : 'entry';

  return {
    icon,
    title: <SendToDocumentSidebarItemTitle step={step} />,
    mainViewItem: {type: AnalysisMainViewItemType.SEND_TO_DOCUMENT, stepId: step.id},
  };
}

/**
 * @deprecated
 */
export function getOutputSidebarItemDatas(analysis: AnalysisGraphQL): Array<SidebarItemData> {
  return analysis.execution.steps
    .filter(
      (step) =>
        step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET ||
        step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT
    )
    .map((step) =>
      step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET
        ? getExportDataCatalogDatasetSidebarItemData(step)
        : getSendToDocumentSidebarItemData(step)
    );
}
/**
 * @deprecated
 */
export function getResultSidebarItemDatas(analysis: AnalysisGraphQL): Array<SidebarItemData> {
  return analysis.execution.steps
    .filter((step) => step.definition.stepType === PipelineStepType.SAVE_TO_RESULTS)
    .map((step) => getSaveToResultsSidebarItemData(step));
}

/**
 * @deprecated
 */
export function getLegacyParameterOptimizationSidebarItemDatasForStep(
  step: GetAnalysisPipelineStep
): Array<SidebarItemData> {
  if (step.statusCode === PipelineStepStatusCode.RUNNING) {
    if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION) {
      return [
        getRunningSidebarItemData(step),
        {
          icon: 'settings',
          title: 'View configuration',
          mainViewItem: {
            type: AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
            stepId: step.id,
          },
        },
      ];
    } else {
      return [getRunningSidebarItemData(step)];
    }
  } else if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return [getFailedSidebarItemData(step)];
  } else if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.SUCCEEDED:
        return [
          {
            icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
            title: 'Data summary',
            mainViewItem: {
              type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD,
              stepId: step.id,
            },
          },
          {
            icon: 'settings',
            title: 'View configuration',
            mainViewItem: {
              type: AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
              stepId: step.id,
            },
          },
        ];
      default:
        assertAndContinue(
          false,
          'Unexpected status for PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_MODELING) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.BLOCKED:
        return [];
      case PipelineStepStatusCode.SUCCEEDED:
        return getSucceededModelingSidebarItemDatas(step);
      default:
        assertAndContinue(
          false,
          'Unexpected status for PARAMETER_OPTIMIZATION_MODELING',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  } else if (step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION) {
    switch (step.statusCode) {
      case PipelineStepStatusCode.BLOCKED:
        return [];
      case PipelineStepStatusCode.SUCCEEDED:
        return getSucceededPredictionSidebarItemDatas(step);
      default:
        assertAndContinue(
          false,
          'Unexpected status for PARAMETER_OPTIMIZATION_PREDICTION',
          pickDefined(step, 'statusCode', 'definition')
        );
        return [];
    }
  }
  return [];
}

function getExploratoryDataAnalysisSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  if (
    step.statusCode === PipelineStepStatusCode.RUNNING ||
    step.statusCode === PipelineStepStatusCode.WAITING ||
    step.statusCode === PipelineStepStatusCode.BLOCKED
  ) {
    return getRunningSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return getFailedSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    return {
      icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
      title: 'Exploratory Data Analysis',
      mainViewItem: {type: AnalysisMainViewItemType.EXPLORATORY_DATA_ANALYSIS, stepId: step.id},
    };
  } else {
    assertAndContinue(
      false,
      'Unexpected status for EXPLORATORY_DATA_ANALYSIS_BETA',
      pickDefined(step, 'statusCode', 'definition')
    );
    return getFailedSidebarItemData(step);
  }
}

export function getModelingAndPredictionSidebarItemDatas(
  modelingStep: GetAnalysisPipelineStep,
  predictionStep: GetAnalysisPipelineStep
): Array<SidebarItemData> {
  if (modelingStep.statusCode === PipelineStepStatusCode.FAILED) {
    return [getFailedSidebarItemData(modelingStep)];
  } else if (modelingStep.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    if (predictionStep.statusCode === PipelineStepStatusCode.FAILED) {
      return [
        ...getSucceededModelingSidebarItemDatas(modelingStep),
        getFailedSidebarItemData(predictionStep),
      ];
    } else if (predictionStep.statusCode === PipelineStepStatusCode.SUCCEEDED) {
      return [
        ...getSucceededModelingSidebarItemDatas(modelingStep),
        ...getSucceededPredictionSidebarItemDatas(predictionStep),
      ];
    } else {
      return [
        ...getSucceededModelingSidebarItemDatas(modelingStep),
        getRunningSidebarItemData(predictionStep),
      ];
    }
  } else if (predictionStep.statusCode === PipelineStepStatusCode.FAILED) {
    return [getFailedSidebarItemData(predictionStep)];
  } else {
    return [
      {
        icon: 'request-in-progress',
        title: 'Model training in progress...',
        mainViewItem: {
          type: AnalysisMainViewItemType.RUNNING,
          stepId: modelingStep.id,
        },
      },
    ];
  }
}

/**
 * @deprecated
 */
export function getLegacyParameterOptimizationSidebarItemDatasForAnalysis(
  analysis: AnalysisGraphQL
): Array<SidebarItemData> {
  return analysis.execution.steps
    .filter(
      (step) =>
        isParameterOptimizationStepType(step.definition.stepType) &&
        getParentSteps(analysis, step).length === 0
    )
    .flatMap((step) => getLegacyParameterOptimizationSidebarItemDatasForStep(step));
}

/**
 * @deprecated
 */
export function getParameterOptimizationConfigurationSidebarItemData(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): SidebarItemData {
  if (
    step.statusCode === PipelineStepStatusCode.WAITING ||
    step.statusCode === PipelineStepStatusCode.RUNNING
  ) {
    return {
      icon: 'request-in-progress',
      title: 'Loading Parameter Optimization...',
      mainViewItem: {
        type: AnalysisMainViewItemType.RUNNING,
        stepId: step.id,
      },
    };
  } else if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return getFailedSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const exploratoryDataAnalysisStep = getChildSteps(analysis, step).find(
      (childStep) => childStep.definition.stepType === PipelineStepType.EXPLORATORY_DATA_ANALYSIS
    );
    const modelingStep = getChildSteps(analysis, step).find(
      (childStep) => childStep.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_MODELING
    );
    const predictionStep = modelingStep
      ? getChildSteps(analysis, modelingStep).find(
          (childStep) => childStep.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION
        )
      : null;
    assertAndContinue(
      modelingStep != null,
      'Expected to find a modeling step for a successful PO configuration step'
    );
    assertAndContinue(
      predictionStep != null,
      'Expected to find a prediction step for a successful PO configuration step'
    );

    const modelingAndPredictionSidebarItemDatas =
      modelingStep && predictionStep
        ? getModelingAndPredictionSidebarItemDatas(modelingStep, predictionStep)
        : [];

    const exploratoryDataAnalysisSidebarItemDatas = exploratoryDataAnalysisStep
      ? [getExploratoryDataAnalysisSidebarItemData(exploratoryDataAnalysisStep)]
      : [];
    return {
      icon: 'table',
      title: step.outputStepDatas[0].dataFrames[0].name,
      mainViewItem: {
        type: AnalysisMainViewItemType.DATA_FRAME,
        stepId: step.id,
        dataFrameId: step.outputStepDatas[0].dataFrames[0].id,
      },
      childSidebarItems: [
        ...exploratoryDataAnalysisSidebarItemDatas,
        ...modelingAndPredictionSidebarItemDatas,
      ],
    };
  } else {
    assertAndContinue(
      false,
      'Unexpected status for PARAMETER_OPTIMIZATION_CONFIGURATION',
      pickDefined(step, 'statusCode', 'definition')
    );
    return getFailedSidebarItemData(step);
  }
}

/**
 * @deprecated
 */
export function getParameterOptimizationConfigurationSidebarItemDatas(
  analysis: AnalysisGraphQL
): Array<SidebarItemData> {
  return analysis.execution.steps
    .filter((step) => step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION)
    .map((step) => getParameterOptimizationConfigurationSidebarItemData(analysis, step));
}

/**
 * @deprecated
 */
export function getLegacyAnalysisSidebarItemDatas(analysis: AnalysisGraphQL): Array<SidebarItemData> {
  return [
    ...getDatasetSectionSidebarItemDatas(analysis),
    ...getFilesSectionSidebarItemDatas(analysis),
    ...getResultSidebarItemDatas(analysis),
    ...getOutputSidebarItemDatas(analysis),
    ...getLegacyParameterOptimizationSidebarItemDatasForAnalysis(analysis),
    ...getParameterOptimizationConfigurationSidebarItemDatas(analysis),
  ];
}

/*
 *********************************
 * Start of the new sidebar code *
 *********************************
 */

interface ViewGrouping {
  parentViewId: string;
  title: string;
  icon: BenchlingIconName;
  childViews: Array<AnalysisViewGraphQL>;
}

/**
 * Overrides for how to render views in the sidebar.
 */
export interface ViewSidebarOverrides {
  /**
   * Set of view IDs to skip rendering as normal children.
   */
  skipViewIds: Set<string>;
  viewGroupings: Array<ViewGrouping>;
}

/**
 * Generate the viewSidebarOverrides object used to modify the sidebar structure.
 *
 * In particular, we need special handling for layout views. For layouts, we check if the layout has a
 * `sidebarParent` config, and if it does skip rendering all ancestors until that sidebarParent.
 *
 * We also create view groupings for EO layouts to better organize them.
 */
export function getViewSidebarOverrides(analysis: AnalysisGraphQL): ViewSidebarOverrides {
  const skipViewIds = new Set<string>();
  const viewGroupings: Array<ViewGrouping> = [];

  const layoutViews = analysis.views.filter(
    (view) =>
      view.lineage.length > 0 &&
      view.lineage[view.lineage.length - 1].step.stepType === PipelineStepType.LAYOUT
  );

  const layoutGroups = getLayoutViewGroups(analysis);
  Array.from(layoutGroups.values()).forEach((layoutGroup) => {
    layoutGroup.views.forEach((view) => {
      skipViewIds.add(getDefaultClientIdForView(view));
    });

    const displayName = layoutGroup.displayName;
    viewGroupings.push({
      parentViewId: getLayoutSidebarParentViewId(analysis, layoutGroup.views[0])!,
      title: displayName,
      icon: 'wave-sine',
      childViews: layoutGroup.views,
    });
  });

  layoutViews.forEach((layoutView) => {
    const sidebarParentViewId = getLayoutSidebarParentViewId(analysis, layoutView);
    if (sidebarParentViewId == null) {
      return;
    }

    // Run a DFS to hide all views between the layout and sidebar parent views
    const ancestorViews = getParentViewsForView(analysis, layoutView);
    const seenViewIds = new Set();
    while (ancestorViews.length > 0) {
      const ancestorView = ancestorViews.pop()!;
      const ancestorViewId = getDefaultClientIdForView(ancestorView);
      if (ancestorViewId === sidebarParentViewId || seenViewIds.has(ancestorViewId)) {
        continue;
      }

      skipViewIds.add(ancestorViewId);
      seenViewIds.add(ancestorViewId);
      ancestorViews.push(...getParentViewsForView(analysis, ancestorView));
    }
  });

  return {
    skipViewIds,
    viewGroupings,
  };
}

function getLayoutSidebarParentViewId(
  analysis: AnalysisGraphQL,
  layoutView: AnalysisViewGraphQL
): string | null {
  const layoutStep = layoutView.lineage[layoutView.lineage.length - 1].step;
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);
  const sidebarParentReference = layoutStep.config?.sidebarParent;
  const sidebarParentEdge = layoutStep.incomingEdges.find(
    (incomingEdge) =>
      incomingEdge.toStepData.name === sidebarParentReference?.inputDataName &&
      incomingEdge.toStepDataIndex === sidebarParentReference?.inputDataIndex
  );
  if (sidebarParentEdge == null) {
    return null;
  }

  const sidebarParentView = getViewForViewReference(analysis, {
    stepId: sidebarParentEdge.fromStep.id,
    outputReference: {
      stepDataName: sidebarParentEdge.fromStepData.name,
      rawDataIndex: sidebarParentEdge.fromStepDataIndex,
    },
  });
  if (sidebarParentView == null) {
    return null;
  }
  return getDefaultClientIdForView(sidebarParentView);
}

/**
 * Returns the sidebar item data that is rooted with the given view.
 *
 * This should be used for the new view-based behavior defined by `isAnalysisToolRedesignEnabled`
 *
 * TODO(BNCH-125215): Support outputs directly here, without their own separate sections
 *
 * @param view The view to get sidebar item data for
 * @param analysis The analysis containing the view
 * @param viewSidebarOverrides Optional overrides for determining which child views to display
 * @returns The sidebar item data for the view
 */
export function getAnalysisViewSidebarItemData(
  view: AnalysisViewGraphQL,
  analysis: AnalysisGraphQL,
  viewSidebarOverrides?: ViewSidebarOverrides
): SidebarItemData {
  const viewId = getDefaultClientIdForView(view);
  const {lineage} = view;
  const lastNode = lineage[lineage.length - 1];
  const isLayoutView = lastNode.step.stepType === PipelineStepType.LAYOUT;

  if (lastNode.stepOutputReference == null && !isLayoutView) {
    return {
      icon: SIDEBAR_FAILED_ICON,
      title: 'Failed view',
      mainViewItem: {
        type: AnalysisMainViewItemType.FAILED,
        stepId: lastNode.step.id,
      },
    };
  }

  const icon = getIconForView(analysis, view);
  const title = getTitleForView(view);

  const isFlaggingRows = view.lineage.some((node) => node.step.stepType === PipelineStepType.FLAG_ROWS);

  const childViews = getChildViewsForView(analysis, view).filter((childView) => {
    const childViewId = getDefaultClientIdForView(childView);
    if (viewSidebarOverrides?.skipViewIds.has(childViewId)) {
      return false;
    }

    const childLastNode = childView.lineage[childView.lineage.length - 1];
    return (
      !isTopLevelAnalysisView(childView) &&
      !isLegacyTopLevelAnalysisView(childView) &&
      // TODO(BNCH-125769): Remove this logic
      // Regressions currently generate 3 views, but we only want to show one item in the sidebar. Without
      // layouts, the easiest thing is to filter out 2 of the views and render the old REGRESSION view
      (childLastNode.step.stepType !== PipelineStepType.REGRESSION ||
        childLastNode.stepOutputReference?.outputStepData.name === 'Output chart')
    );
  });

  const childViewGroupings =
    viewSidebarOverrides?.viewGroupings.filter((viewGrouping) => viewGrouping.parentViewId === viewId) ?? [];

  const childSidebarItems = childViews
    .map((childView) => getAnalysisViewSidebarItemData(childView, analysis, viewSidebarOverrides))
    .concat(
      childViewGroupings.map((viewGrouping) => ({
        icon: viewGrouping.icon,
        title: viewGrouping.title,
        mainViewItem: getMainViewItemForView(viewGrouping.childViews[0]),
        childSidebarItems: viewGrouping.childViews.map((childView) =>
          getAnalysisViewSidebarItemData(childView, analysis, viewSidebarOverrides)
        ),
      }))
    );

  const mainViewItem: AnalysisMainViewItem = getMainViewItemForView(view);

  return {
    icon,
    title,
    isFlaggingRows,
    // Mark views with a failed parent view as inactive, to indicate to the user that opening the view is
    // inactionable. We don't do this for layouts since layouts may control the status of their parent views.
    inactive: !allParentViewsSucceeded(analysis, view) && !isLayoutView,
    mainViewItem,
    actionButtonOptions:
      isTopLevelAnalysisView(view) &&
      lastNode.stepOutputReference != null &&
      lastNode.stepOutputReference.rawDataIndex != null &&
      lastNode.stepOutputReference.outputStepData.rawDatas[lastNode.stepOutputReference.rawDataIndex]
        .__typename === 'DataFrameDomainNode'
        ? {
            stepDataReference: {
              stepDataName: lastNode.stepOutputReference.outputStepData.name,
              stepId: lastNode.step.id,
            },
            dataFrameId:
              lastNode.stepOutputReference.outputStepData.rawDatas[lastNode.stepOutputReference.rawDataIndex]
                .id,
          }
        : undefined,
    childSidebarItems,
  };
}
